/* eslint-disable camelcase */
import cx from "classnames";
// TODO port this somewhere useful
import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import { CTA } from "common/styleguide/CTA";
import { SectionBox } from "common/styleguide/SectionBox";
import { Map, OrderedMap, Record } from "immutable";
import fetchJSON from "lib/fetchJSON";
import React, { Fragment } from "react";
import { AdminLayout, Grid } from "../layoutComponents";
import styles from "./AdminExperiments.scss";

const Experiment = Record({
  id: null,
  name: null,
  description: null,
  enable_for_admins: null,
  enable_for_development: null,
  admin_only: null,
  rollout_percentage: null,
  created_at: null,
  updated_at: null,
  override_counts: {},
  current_user_has_override: null,
});

export class AdminExperiments extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      experiments: [],
    };
  }

  componentDidMount() {
    this.getData();
  }

  getData = () => {
    fetchJSON("/admin/experiments.json")
      .then((experiments) => {
        this.setState({
          experiments: experiments.map(Experiment),
          loading: false,
        });
      })
      .catch((e) => console.error(JSON.stringify(e, null, "  ")));
  };

  refresh = () => {
    this.setState({
      loading: true,
      experiments: [],
    });
    this.getData();
  };

  newExperiment = () => {
    const name = window.prompt("New Experiment Name");
    if (!name) {
      return;
    }
    fetchJSON("/admin/experiments.json", { experiment: { name } }).then(this.refresh);
  };

  render() {
    const { loading, experiments } = this.state;

    if (loading) {
      return <AdminLayout loading title="RWGPS Labs" />;
    }
    return (
      <AdminLayout title="RWGPS Labs">
        <p>
          <CTA secondary inline margins={false} onClick={this.newExperiment}>
            New Experiment
          </CTA>
        </p>
        <Grid cols={2}>
          {experiments.map((e) => (
            <ExperimentItem key={e.id} e={e} reload={this.reload} />
          ))}
        </Grid>
      </AdminLayout>
    );
  }
}

const EXP_OPTIONS = OrderedMap({
  enable_for_admins: "Enable for all admins",
  enable_for_development: "Enable for RAILS_ENV development",
  admin_only: "Restrict opt-in to admins only",
});

class ExperimentItem extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showRaw: false,
      pendingEdits: Map(),
      confirmReset: null,
      optResetPending: null,
    };
  }

  getSelectedOpts = () => {
    const { e } = this.props;
    return Set(EXP_OPTIONS.keySeq().filter((k) => e[k]));
  };

  toggleRaw = () => {
    this.setState({ showRaw: !this.state.showRaw });
  };

  onChangeCheckbox = (key, e) => {
    const { pendingEdits } = this.state;
    const checked = e.target.checked;
    this.setState({ pendingEdits: pendingEdits.merge({ [key]: checked }) });
  };

  onChangeRollout = (e) => {
    const { pendingEdits } = this.state;
    const rollout_percentage = e.target.value;
    this.setState({ pendingEdits: pendingEdits.merge({ rollout_percentage }) });
  };

  onSave = () => {
    const { pendingEdits } = this.state;
    const { e } = this.props;
    if (pendingEdits.size === 0) {
      return;
    }

    fetchJSON(`/admin/experiments/${e.id}.json`, { experiment: pendingEdits.toJSON() }, { method: "PATCH" })
      .then(() => window.location.reload())
      .catch((e) => {
        window.alert("Encountered error: " + e.message);
        console.error(e);
      });
  };

  optOut = () => {
    const { e } = this.props;
    fetchJSON(`/experiments/${e.id}/opt_out.json`, {}, { method: "POST" }).then(() => window.location.reload());
  };

  optIn = () => {
    const { e } = this.props;
    fetchJSON(`/experiments/${e.id}/opt_in.json`, {}, { method: "POST" }).then(() => window.location.reload());
  };

  removeOverride = () => {
    const { e } = this.props;
    fetchJSON(`/experiments/${e.id}/opt_default.json`, {}, { method: "POST" }).then(() => window.location.reload());
  };

  confirmReset = (type) => {
    this.setState({ confirmReset: type });
  };

  enactOptReset = () => {
    const { e } = this.props;
    const { confirmReset } = this.state;
    this.setState({ optResetPending: true });
    fetchJSON(`/experiments/${e.id}/reset_${confirmReset}.json`, {}, { method: "POST" }).then(() =>
      window.location.reload()
    );
  };

  render() {
    const { e } = this.props;
    const { showRaw, pendingEdits, confirmReset, optResetPending } = this.state;
    const exp = e.merge(pendingEdits);
    const enabled = window.rwgps.config.labs[exp.name];
    const enabled_text = enabled ? "enabled" : "disabled";
    const optInCount = exp.override_counts.enabled.toLocaleString("en-US");
    const optOutCount = exp.override_counts.disabled.toLocaleString("en-US");

    return (
      <SectionBox className={styles.Experiment}>
        <div className={styles.header}>
          <h2>{exp.name}</h2>
          <div>
            {exp.current_user_has_override && (
              <span
                className={cx(styles.status, styles[enabled_text])}
                title={`This feature is ${enabled_text} due to an override placed on your user account`}
              >
                override:{enabled_text}
              </span>
            )}
            {!exp.current_user_has_override && enabled && (
              <span className={styles.status} title="Your account is included in the rollout criteria for this feature">
                rolled out to your account
              </span>
            )}
          </div>
        </div>
        <form>
          <label>
            <input
              type="checkbox"
              checked={exp.enable_for_admins}
              onChange={this.onChangeCheckbox.bind(this, "enable_for_admins")}
            />
            Enable for all admins
          </label>
          <label>
            <input type="checkbox" checked={exp.admin_only} onChange={this.onChangeCheckbox.bind(this, "admin_only")} />
            Restrict opt-in to admins only
          </label>
          <label htmlFor={"rollout_percentage"}>
            <strong>Rollout: {exp.rollout_percentage}%</strong>
          </label>
          <input
            id={"rollout_percentage"}
            type="range"
            onChange={this.onChangeRollout}
            value={exp.rollout_percentage}
            step="5"
            min="0"
            max="100"
          />
        </form>
        <div className={styles.flexes}>
          <div>
            {pendingEdits.size > 0 && (
              <CTA onClick={this.onSave} inline margins={false}>
                Save
              </CTA>
            )}
          </div>
          <div className={styles.actionLinks}>
            {exp.current_user_has_override && (
              <CTA link inline margins={false} onClick={this.removeOverride}>
                Remove Override
              </CTA>
            )}
            {enabled && (
              <CTA link inline margins={false} onClick={this.optOut}>
                Opt Out
              </CTA>
            )}
            {!enabled && (
              <CTA link inline margins={false} onClick={this.optIn}>
                Opt In
              </CTA>
            )}
            {/* <a onClick={this.toggleRaw}>Toggle Raw</a> */}
          </div>
        </div>
        {showRaw && <pre>{JSON.stringify(e, null, "  ")}</pre>}
        <div className={styles.optInStats}>
          <div className={styles.optInfo}>
            <strong>{optInCount}</strong> Opt-ins
            <a className={styles.resetOpt} onClick={() => this.confirmReset("opt_ins")}>
              Reset
            </a>
          </div>
          <div className={styles.optInfo}>
            <strong>{optOutCount}</strong> Opt-outs
            <a className={styles.resetOpt} onClick={() => this.confirmReset("opt_outs")}>
              Reset
            </a>
          </div>
        </div>
        {confirmReset && (
          <ConfirmationDialog
            danger
            onCancel={optResetPending ? undefined : () => this.setState({ confirmReset: null })}
            onRequestClose={optResetPending ? undefined : () => this.setState({ confirmReset: null })}
            onConfirm={this.enactOptReset}
            confirmPending={optResetPending}
            confirmText={`Reset ${confirmReset}`}
            title={
              <Fragment>
                Reset all {confirmReset} for <strong>{exp.name}</strong>?
              </Fragment>
            }
          >
            Are you sure you want to reset {confirmReset === "opt_ins" ? optInCount : optOutCount} user {confirmReset}{" "}
            for the experiment '{exp.name}'?
          </ConfirmationDialog>
        )}
      </SectionBox>
    );
  }
}
